/**
 * Celebrations (+field): the level-up "ding" and its smaller cousins, world-space so everyone
 * nearby sees them. One celebration is a short timeline around a burst moment:
 *
 *   gather   a golden rune circle spins up under the hero, sparkles spiral in to the feet;
 *   burst    a pillar of light shoots into the sky, a white-gold flash at the chest, ground
 *            rings and a shock band race outward (pushing the grass), sparks fountain up;
 *   sustain  the column streams upward with sparkles spiralling round it, then thins away.
 *
 * Sizes: 'small' (bounty turn-in: circle, rings, sparks, a short beam), 'big' (a level, a first
 * clear), 'huge' (several levels at once: taller, wider, a second burst). Presentation-only
 * outputs for the renderer: `exposure` (tone-mapping exposure boost), `bloom` (bloom strength
 * boost), `fov` (degrees of zoom-in punch) and the grass shockwave; the burst callback lets the
 * renderer play the impact sound and a short hit-stop (never the sim).
 * Every mesh is additive and moderate; it scales with the effects-intensity setting.
 */
import * as THREE from 'three';
import { NOISE_GLSL } from '../style';
import { GRASS_SHOCK } from '../outdoor/field';

export type CelebrationSize = 'small' | 'big' | 'huge';

interface SizeSpec {
  burstAt: number;
  life: number;
  pillarH: number;
  pillarR: number;
  rings: number;
  ringR: number;
  sparks: number;
  spiral: number;
  exposure: number;
  bloom: number;
  fov: number;
  shock: number;
  /** Second burst (s after the first), 0 = none. */
  rebound: number;
}

export const CELEBRATION: Readonly<Record<CelebrationSize, SizeSpec>> = {
  small: { burstAt: 0.18, life: 1.7, pillarH: 7, pillarR: 0.55, rings: 2, ringR: 6, sparks: 50, spiral: 40, exposure: 0.12, bloom: 0.15, fov: 0, shock: 0.5, rebound: 0 },
  big: { burstAt: 0.34, life: 3.4, pillarH: 30, pillarR: 1.0, rings: 3, ringR: 13, sparks: 110, spiral: 140, exposure: 0.42, bloom: 0.45, fov: 4.5, shock: 1.0, rebound: 0 },
  huge: { burstAt: 0.34, life: 4.4, pillarH: 40, pillarR: 1.35, rings: 5, ringR: 17, sparks: 170, spiral: 220, exposure: 0.55, bloom: 0.6, fov: 6, shock: 1.3, rebound: 0.62 },
};

const GOLD = new THREE.Color(1.0, 0.78, 0.32);
const PALE = new THREE.Color(1.0, 0.95, 0.78);
const MAX_P = 420;

// ---------------------------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------------------------

const BEAM_VERT = `varying vec2 vUv; varying float vRim;
  void main(){ vUv = uv; vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vec3 n = normalize(normalMatrix * normal); vRim = 1.0 - abs(dot(n, normalize(-mv.xyz)));
    gl_Position = projectionMatrix * mv; }`;
const BEAM_FRAG = `uniform float uT; uniform float uA; uniform vec3 uC; uniform float uSpeed; uniform float uSeed; varying vec2 vUv; varying float vRim;
  ${NOISE_GLSL}
  void main(){
    float y = vUv.y;
    float streak = noise(vec2(vUv.x * 18.0 + uSeed, y * 3.0 - uT * uSpeed));
    float streak2 = noise(vec2(vUv.x * 7.0 - uSeed, y * 1.4 - uT * uSpeed * 0.6));
    float vf = smoothstep(0.0, 0.04, y) * (1.0 - smoothstep(0.55, 1.0, y));
    // vRim can dip a hair below 0 (|dot| > 1 by rounding, interpolation): pow() of a negative base
    // is NaN, and one NaN pixel blacks out whole bloom tiles. Clamp it.
    float rim = clamp(vRim, 0.0, 1.0);
    float core = 0.25 + 0.75 * pow(1.0 - rim, 0.6);
    float a = (0.3 + 0.7 * streak * streak2 * 1.6) * vf * mix(core, pow(rim, 1.4), 0.35) * uA;
    vec3 col = mix(uC, vec3(1.0, 0.98, 0.9), clamp(pow(streak, 3.0) * 0.8 + (1.0 - y) * 0.25, 0.0, 1.0));
    gl_FragColor = vec4(col * a, a);
  }`;

const GLYPH_FRAG = `uniform float uT; uniform float uA; uniform vec3 uC; varying vec2 vUv;
  void main(){
    vec2 p = vUv * 2.0 - 1.0; float r = length(p); if (r > 1.0) discard;
    float ang = atan(p.y, p.x);
    float a1 = ang + uT * 0.9; float a2 = ang - uT * 0.6;
    float ring = smoothstep(0.022, 0.0, abs(r - 0.92)) + smoothstep(0.016, 0.0, abs(r - 0.8)) * 0.8 + smoothstep(0.012, 0.0, abs(r - 0.42)) * 0.7;
    float ticks = step(0.6, fract(a1 * 36.0 / 6.2831)) * step(0.81, r) * step(r, 0.9);
    float runes = step(0.5, fract(a2 * 12.0 / 6.2831 + 0.25)) * step(0.5, fract(r * 9.0)) * step(0.46, r) * step(r, 0.78) * 0.45;
    float star = 0.0;
    for (int i = 0; i < 6; i++) { float b = float(i) * 1.0472 + uT * 0.4; vec2 d = vec2(cos(b), sin(b)); star += smoothstep(0.014, 0.0, abs(p.x * d.y - p.y * d.x)) * step(dot(p, d), 0.0) * step(r, 0.8); }
    float glow = pow(max(0.0, 1.0 - r), 2.5) * 0.6;
    float a = (ring + ticks + runes + star * 0.5 + glow) * uA;
    gl_FragColor = vec4(uC * a, a);
  }`;

const RING_FRAG = `uniform float uR; uniform float uW; uniform float uA; uniform vec3 uC; varying vec2 vUv;
  void main(){
    float r = length(vUv * 2.0 - 1.0);
    float d = (r - uR) / uW;
    float a = exp(-d * d * 3.0) * uA * (r < uR ? 1.0 : 1.0);
    if (a < 0.003) discard;
    gl_FragColor = vec4(mix(uC, vec3(1.0), 0.3 * exp(-d * d * 12.0)) * a, a);
  }`;

const SIMPLE = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const SPARK_VERT = `attribute float aA; attribute float aS; uniform float uPx; varying float vA;
  void main(){ vA = aA; vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_PointSize = aS * uPx / max(0.5, -mv.z); gl_Position = projectionMatrix * mv; }`;
const SPARK_FRAG = `uniform vec3 uC; uniform float uFx; varying float vA;
  void main(){ vec2 d = gl_PointCoord * 2.0 - 1.0; float r = length(d); if (r > 1.0 || vA <= 0.0) discard;
    float core = pow(max(0.0, 1.0 - r), 3.0);
    float cross = max(smoothstep(0.12, 0.0, abs(d.x)), smoothstep(0.12, 0.0, abs(d.y))) * (1.0 - r);
    float a = (core + cross * 0.8) * vA * min(1.2, uFx);
    gl_FragColor = vec4(mix(uC, vec3(1.0), core * 0.6) * a, a); }`;

function additive(frag: string, uniforms: Record<string, THREE.IUniform>, vert = SIMPLE, side: THREE.Side = THREE.DoubleSide): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({ uniforms, vertexShader: vert, fragmentShader: frag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side });
}

// ---------------------------------------------------------------------------------------------
// One celebration
// ---------------------------------------------------------------------------------------------

interface Spark {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  life: number;
  size: number;
  /** 0 = fountain (ballistic, world), 1 = spiral (around the column, follows), 2 = gather (inward). */
  mode: number;
  a: number;
  r: number;
  w: number;
  delay: number;
}

class Celebration {
  readonly group = new THREE.Group();
  age = 0;
  burst = false;
  rebounded = false;
  private readonly outer: THREE.Mesh;
  private readonly inner: THREE.Mesh;
  private readonly glyph: THREE.Mesh;
  private readonly rings: THREE.Mesh[] = [];
  private readonly shock: THREE.Mesh;
  private readonly flash: THREE.Sprite;
  private readonly points: THREE.Points;
  private readonly sparks: Spark[] = [];
  private readonly pos: Float32Array;
  private readonly alpha: Float32Array;
  private readonly size: Float32Array;
  /** Where the burst happened (rings stay there; the column follows the unit). */
  readonly origin = new THREE.Vector3();
  readonly at = new THREE.Vector3();

  constructor(
    readonly spec: SizeSpec,
    readonly unitId: string | null,
    at: THREE.Vector3,
    private readonly shared: CelebrationShared,
    readonly mine: boolean,
  ) {
    this.at.copy(at);
    this.origin.copy(at);
    const beam = (r: number, a: number, speed: number, c: THREE.Color): THREE.Mesh => {
      const m = new THREE.Mesh(shared.cyl, additive(BEAM_FRAG, { uT: { value: 0 }, uA: { value: 0 }, uC: { value: c.clone() }, uSpeed: { value: speed }, uSeed: { value: Math.random() * 10 } }, BEAM_VERT));
      m.scale.set(r, 0.001, r);
      m.renderOrder = 9;
      m.frustumCulled = false;
      void a;
      return m;
    };
    this.outer = beam(spec.pillarR * 2.3, 0.4, 2.2, GOLD);
    this.inner = beam(spec.pillarR, 1, 4.5, PALE);
    this.glyph = new THREE.Mesh(shared.plane, additive(GLYPH_FRAG, { uT: { value: 0 }, uA: { value: 0 }, uC: { value: GOLD.clone() } }));
    this.glyph.renderOrder = 3;
    for (let i = 0; i < spec.rings; i++) {
      const m = new THREE.Mesh(shared.plane, additive(RING_FRAG, { uR: { value: 0 }, uW: { value: 0.05 }, uA: { value: 0 }, uC: { value: (i % 2 ? PALE : GOLD).clone() } }));
      m.renderOrder = 3;
      m.visible = false;
      this.rings.push(m);
    }
    this.shock = new THREE.Mesh(shared.band, additive(BEAM_FRAG, { uT: { value: 0 }, uA: { value: 0 }, uC: { value: PALE.clone() }, uSpeed: { value: 1 }, uSeed: { value: 3 } }, BEAM_VERT));
    this.shock.visible = false;
    this.shock.renderOrder = 9;
    this.flash = new THREE.Sprite(new THREE.SpriteMaterial({ map: shared.glowTex, color: new THREE.Color(1.0, 0.9, 0.65), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 }));
    this.flash.renderOrder = 10;
    this.pos = new Float32Array(MAX_P * 3);
    this.alpha = new Float32Array(MAX_P);
    this.size = new Float32Array(MAX_P);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aA', new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aS', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.points = new THREE.Points(g, shared.sparkMat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10;
    this.group.add(this.glyph, this.outer, this.inner, this.shock, this.flash, this.points, ...this.rings);
    // Gather sparkles (before the burst).
    const gatherN = Math.round(spec.sparks * 0.5);
    for (let i = 0; i < gatherN; i++) this.sparks.push({ x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, age: 0, life: spec.burstAt + 0.05, size: 0.18 + Math.random() * 0.14, mode: 2, a: Math.random() * Math.PI * 2, r: 2.5 + Math.random() * 2.5, w: 3 + Math.random() * 2, delay: Math.random() * spec.burstAt * 0.4 });
  }

  private spawnBurstSparks(k = 1): void {
    const s = this.spec;
    for (let i = 0; i < s.sparks * k; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 3 + Math.random() * 7;
      const up = 5 + Math.random() * 9;
      this.sparks.push({ x: this.origin.x, y: this.origin.y + 0.8, z: this.origin.z, vx: Math.cos(a) * sp, vy: up, vz: Math.sin(a) * sp, age: 0, life: 0.8 + Math.random() * 0.8, size: 0.16 + Math.random() * 0.22, mode: 0, a: 0, r: 0, w: 0, delay: 0 });
    }
    for (let i = 0; i < s.spiral * k; i++) {
      this.sparks.push({ x: 0, y: 0, z: 0, vx: 0, vy: 4 + Math.random() * 7, vz: 0, age: 0, life: 1.4 + Math.random() * 1.4, size: 0.14 + Math.random() * 0.2, mode: 1, a: Math.random() * Math.PI * 2, r: s.pillarR * (1.2 + Math.random() * 1.6), w: (2.2 + Math.random() * 2.5) * (Math.random() < 0.5 ? 1 : -1), delay: Math.random() * Math.min(1.6, s.life - s.burstAt - 1.2) });
    }
  }

  /** Advance; returns false when finished. */
  update(dt: number, follow: THREE.Vector3 | null, fx: number): boolean {
    this.age += dt;
    const s = this.spec;
    const t = this.age;
    if (follow) this.at.copy(follow);
    const tb = s.burstAt;
    const after = t - tb;
    const k = Math.min(1.2, fx);

    // Rune circle: spins up, flares at the burst, fades out.
    const gIn = Math.min(1, t / Math.max(0.05, tb));
    const gOut = 1 - smooth(s.life * 0.6, s.life * 0.92, t);
    const flare = after > 0 ? Math.exp(-after * 5) * 0.8 : 0;
    const gr = (s.pillarR * 3.2 + 0.8) * (0.4 + 0.6 * easeOut(gIn));
    this.glyph.position.set(this.at.x, this.at.y + 0.06, this.at.z);
    this.glyph.scale.setScalar(gr);
    const gu = (this.glyph.material as THREE.ShaderMaterial).uniforms;
    gu['uT']!.value = t * (1 + flare * 3);
    gu['uA']!.value = (0.55 * gIn + flare) * gOut * k;

    // Column: shoots up at the burst, streams, thins away.
    const grow = after > 0 ? easeOutExpo(Math.min(1, after / 0.2)) : 0;
    const thin = 1 - smooth(s.life * 0.55, s.life * 0.95, t);
    for (const [m, rk, ak] of [
      [this.inner, 1, 1.0],
      [this.outer, 1, 0.42],
    ] as const) {
      m.position.copy(this.at);
      const widen = 1 + (after > 0 ? Math.exp(-after * 6) * 0.8 : 0);
      m.scale.set((m === this.inner ? s.pillarR : s.pillarR * 2.3) * rk * widen * (0.35 + 0.65 * thin), Math.max(0.001, s.pillarH * grow), (m === this.inner ? s.pillarR : s.pillarR * 2.3) * rk * widen * (0.35 + 0.65 * thin));
      m.rotation.y += dt * (m === this.inner ? 0.8 : -0.5);
      const u = (m.material as THREE.ShaderMaterial).uniforms;
      u['uT']!.value = t;
      u['uA']!.value = ak * grow * thin * k * (1 + flare * 0.6);
      m.visible = grow > 0;
    }

    // Burst moment.
    if (!this.burst && after >= 0) {
      this.burst = true;
      this.spawnBurstSparks();
      for (const r of this.rings) r.visible = true;
      this.shock.visible = true;
    }
    if (s.rebound > 0 && !this.rebounded && after >= s.rebound) {
      this.rebounded = true;
      this.spawnBurstSparks(0.6);
    }

    // Flash at the chest.
    const fa = after >= 0 ? (after < 0.07 ? after / 0.07 : Math.exp(-(after - 0.07) * 6)) : 0;
    const fb = s.rebound > 0 && after >= s.rebound ? Math.exp(-(after - s.rebound) * 7) * 0.7 : 0;
    this.flash.position.set(this.at.x, this.at.y + 1.3, this.at.z);
    this.flash.scale.setScalar((2 + 7 * Math.max(fa, fb)) * (0.6 + s.pillarR * 0.4));
    this.flash.material.opacity = Math.max(fa, fb) * 0.9 * k;

    // Ground rings + shock band (stay where the burst happened).
    this.rings.forEach((m, i) => {
      const delay = i * 0.09 + (s.rebound > 0 && i >= 3 ? s.rebound - 0.27 : 0);
      const rt = after - delay;
      const u = (m.material as THREE.ShaderMaterial).uniforms;
      if (rt < 0) {
        u['uA']!.value = 0;
        return;
      }
      const life = 0.9 + i * 0.12;
      const p = Math.min(1, rt / life);
      const R = s.ringR * (0.6 + i * 0.18) * easeOut(p) + 0.5;
      m.position.set(this.origin.x, this.origin.y + 0.07 + i * 0.004, this.origin.z);
      m.scale.setScalar(R + 1);
      u['uR']!.value = R / (R + 1);
      u['uW']!.value = (0.06 + 0.05 * (1 - p)) * (6 / (R + 1));
      u['uA']!.value = (1 - p) * (1 - p) * 1.1 * k;
    });
    {
      const p = Math.min(1, Math.max(0, after) / 0.55);
      const R = 0.5 + s.ringR * 0.75 * easeOut(p);
      this.shock.position.copy(this.origin);
      this.shock.scale.set(R, 1.4 * (1 - p * 0.6), R);
      const u = (this.shock.material as THREE.ShaderMaterial).uniforms;
      u['uT']!.value = t;
      u['uA']!.value = after >= 0 ? (1 - p) * 0.9 * k : 0;
      this.shock.visible = after >= 0 && p < 1;
      if (this.mine || this.unitId === null) GRASS_SHOCK.value.set(this.origin.x, this.origin.z, R, after >= 0 && p < 1 ? (1 - p) * s.shock : 0);
    }

    // Sparks.
    let n = 0;
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const sp = this.sparks[i]!;
      if (sp.delay > 0) {
        sp.delay -= dt;
        continue;
      }
      sp.age += dt;
      if (sp.age >= sp.life) {
        this.sparks[i] = this.sparks[this.sparks.length - 1]!;
        this.sparks.pop();
        continue;
      }
      const lt = sp.age / sp.life;
      let x: number;
      let y: number;
      let z: number;
      if (sp.mode === 0) {
        sp.vy -= 14 * dt;
        sp.vx *= 1 - dt * 1.8;
        sp.vz *= 1 - dt * 1.8;
        sp.x += sp.vx * dt;
        sp.y += sp.vy * dt;
        sp.z += sp.vz * dt;
        if (sp.y < this.origin.y + 0.05) {
          sp.y = this.origin.y + 0.05;
          sp.vy *= -0.35;
        }
        x = sp.x;
        y = sp.y;
        z = sp.z;
      } else if (sp.mode === 1) {
        sp.a += sp.w * dt;
        const rr = sp.r * (1 + lt * 0.5);
        x = this.at.x + Math.cos(sp.a) * rr;
        z = this.at.z + Math.sin(sp.a) * rr;
        y = this.at.y + 0.2 + sp.vy * sp.age;
      } else {
        sp.a += sp.w * dt;
        const rr = sp.r * (1 - lt) + 0.25;
        x = this.at.x + Math.cos(sp.a) * rr;
        z = this.at.z + Math.sin(sp.a) * rr;
        y = this.at.y + 0.1 + lt * 1.2;
      }
      if (n >= MAX_P) continue;
      this.pos[n * 3] = x;
      this.pos[n * 3 + 1] = y;
      this.pos[n * 3 + 2] = z;
      this.alpha[n] = Math.sin(Math.min(1, lt) * Math.PI) * (0.75 + 0.25 * Math.sin(sp.age * 30 + i));
      this.size[n] = sp.size;
      n++;
    }
    const g = this.points.geometry;
    g.setDrawRange(0, n);
    (g.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
    (g.attributes['aA'] as THREE.BufferAttribute).needsUpdate = true;
    (g.attributes['aS'] as THREE.BufferAttribute).needsUpdate = true;
    return t < s.life;
  }

  /** Exposure / bloom / fov envelope at this moment (0..1 of the spec's peak). */
  envelope(): number {
    const after = this.age - this.spec.burstAt;
    if (after < 0) return 0;
    const a = after < 0.06 ? after / 0.06 : Math.exp(-(after - 0.06) / 0.28);
    const r = this.spec.rebound > 0 && after >= this.spec.rebound ? 0.6 * Math.exp(-(after - this.spec.rebound) / 0.25) : 0;
    return Math.max(a, r);
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | undefined;
      if (m && m !== this.shared.sparkMat) m.dispose();
    });
    this.points.geometry.dispose();
    this.group.removeFromParent();
  }
}

interface CelebrationShared {
  cyl: THREE.BufferGeometry;
  band: THREE.BufferGeometry;
  plane: THREE.BufferGeometry;
  glowTex: THREE.Texture;
  sparkMat: THREE.ShaderMaterial;
}

// ---------------------------------------------------------------------------------------------
// Director
// ---------------------------------------------------------------------------------------------

export class Celebrations {
  readonly group = new THREE.Group();
  /** Presentation outputs for this frame. */
  exposure = 0;
  bloom = 0;
  fov = 0;
  /** Effects intensity (settings). */
  intensity = 1;
  /** Called at each burst: (size, mine). The renderer plays the impact sound, hit-stop, shake. */
  onBurst: ((size: CelebrationSize, mine: boolean, at: THREE.Vector3) => void) | null = null;
  private readonly list: Celebration[] = [];
  private readonly shared: CelebrationShared;
  private readonly burstSeen = new WeakSet<Celebration>();

  constructor(glowTex: THREE.Texture) {
    this.group.name = 'celebrations';
    this.shared = {
      cyl: new THREE.CylinderGeometry(1, 1, 1, 32, 1, true).translate(0, 0.5, 0),
      band: new THREE.CylinderGeometry(1, 1, 1, 48, 1, true).translate(0, 0.5, 0),
      plane: new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2),
      glowTex,
      sparkMat: additive(SPARK_FRAG, { uPx: { value: 900 }, uC: { value: new THREE.Color(1.0, 0.82, 0.4) }, uFx: { value: 1 } }, SPARK_VERT),
    };
  }

  /** Start a celebration on a unit (followed while it lives) at three.js point `at`. */
  spawn(size: CelebrationSize, unitId: string | null, at: THREE.Vector3, mine: boolean): void {
    // Never stack more than 4 (a party levelling together): drop the oldest.
    if (this.list.length >= 4) this.list.shift()!.dispose();
    const c = new Celebration(CELEBRATION[size], unitId, at, this.shared, mine);
    this.list.push(c);
    this.group.add(c.group);
  }

  update(dt: number, follow: (id: string) => THREE.Vector3 | null, camera: THREE.PerspectiveCamera, viewH: number): void {
    this.shared.sparkMat.uniforms['uPx']!.value = (viewH / 2) * camera.projectionMatrix.elements[5]! ;
    this.shared.sparkMat.uniforms['uFx']!.value = this.intensity;
    let exp = 0;
    let bl = 0;
    let fv = 0;
    let anyShock = false;
    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i]!;
      const alive = c.update(dt, c.unitId ? follow(c.unitId) : null, this.intensity);
      if (c.burst && !this.burstSeen.has(c)) {
        this.burstSeen.add(c);
        const size = c.spec === CELEBRATION.huge ? 'huge' : c.spec === CELEBRATION.big ? 'big' : 'small';
        this.onBurst?.(size, c.mine, c.origin);
      }
      const e = c.envelope();
      // Only the local hero's ding (or a world one) flashes the screen; others' are just visible.
      if (c.mine || c.unitId === null) {
        exp = Math.max(exp, e * c.spec.exposure);
        bl = Math.max(bl, e * c.spec.bloom);
        fv = Math.max(fv, e * c.spec.fov);
        anyShock = anyShock || c.age < c.spec.burstAt + 0.6;
      }
      if (!alive) {
        c.dispose();
        this.list.splice(i, 1);
      }
    }
    if (!anyShock) GRASS_SHOCK.value.w = 0;
    const k = Math.min(1.2, this.intensity);
    this.exposure = exp * k;
    this.bloom = bl * k;
    this.fov = fv;
  }

  get active(): number {
    return this.list.length;
  }

  clear(): void {
    for (const c of this.list) c.dispose();
    this.list.length = 0;
    GRASS_SHOCK.value.w = 0;
    this.exposure = this.bloom = this.fov = 0;
  }

  dispose(): void {
    this.clear();
    this.shared.cyl.dispose();
    this.shared.band.dispose();
    this.shared.plane.dispose();
    this.shared.sparkMat.dispose();
    this.group.removeFromParent();
  }
}

/** Celebration size for a level_up event. */
export function levelUpSize(gained: number): CelebrationSize {
  return gained > 1 ? 'huge' : 'big';
}

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
const easeOut = (x: number): number => 1 - (1 - x) ** 3;
const easeOutExpo = (x: number): number => (x >= 1 ? 1 : 1 - Math.pow(2, -10 * x));
